import Vue from 'vue'
import { dispatch, make } from 'vuex-pathify'
import router from '@/router'
import * as firebase from '@/firebase.js'
import * as Units from '@/lib/units';
import * as Objects from '@/lib/objects';
import mixpanel from 'mixpanel-browser';

const state = {
  saveFiles: {},
  saveFilesLoading: false,
  publicSaveFiles: {},
  publicSaveFilesLoading: false,
  saveImages: {},
  saving: false,
  saved: false,
  saving_error: null,
  loading: false,
  activeSaveFileId: null,
}

const getters = {
  ...make.getters([...Object.keys(state)]),
  saveFiles: (state) => {
    return Object.keys(state.saveFiles).map(id => state.saveFiles[id]).sort((a, b) => ((new Date(a.time) > new Date(b.time)) ? -1 : 1));
  },
  saveFilesLoading: (state) => {
    return state.saveFilesLoading && Object.keys(state.saveFiles).length == 0
  },
  publicSaveFiles: (state) => {
    return Object.keys(state.publicSaveFiles).map(id => state.publicSaveFiles[id]).sort((a, b) => (a.name > b.name ? 1 : -1));
  },
  publicSaveFilesLoading: (state) => {
    return state.publicSaveFilesLoading && Object.keys(state.publicSaveFiles).length == 0
  },
  saveFilesById: (state) => (id) => {
    return state.saveFiles[id];
  },
  publicSaveFilesById: (state) => (id) => {
    return state.publicSaveFiles[id];
  },
  getLedger: (state, getters, rootState, rootGetters) => (saveFile) => {
    let ledger = {};

    function addLedgerPartToLedger(ledgerPart){
      if(ledgerPart.id.includes("&")){
        ledgerPart.id.split("&").forEach(id => {
          addLedgerPartToLedger({
            ...ledgerPart,
            id: id
          })
        })
      }else{
        const key = getPartID(ledgerPart);
        if(ledger[key]){
          ledger[key].quantity++; 
        }else{
          ledger[key] = ledgerPart;
        }
      }
    }

    function getPartID(ledgerPart){
      return JSON.stringify({
        ...ledgerPart,
        length: Units.metersToInches(ledgerPart.length, false),
        width: Units.metersToInches(ledgerPart.width, false),
        height: Units.metersToInches(ledgerPart.height, false)
      })
    }
    
    if(saveFile.parts){
      saveFile.parts.forEach(part => {     
        addLedgerPartToLedger(getters.getLedgerPart(part));
        const partMeta = rootGetters['parts/partById'](part.id);
        if(!partMeta || !partMeta.vendor || !partMeta.vendor.fastenersIncluded){
          if(part.snapObjectData.snapAreaData){
            part.snapObjectData.snapAreaData.forEach(area => {
              if(area.snapRegionData){
                area.snapRegionData.forEach(region => {
                  if(region.nodeData){
                    region.nodeData.forEach(node => {
                      if(node.boltSetData){
                        if(node.boltSetData.boltID){
                          addLedgerPartToLedger({ id: node.boltSetData.boltID, quantity: 1 });
                        }
                        if(node.boltSetData.nutID){
                          addLedgerPartToLedger({ id: node.boltSetData.nutID, quantity: 1 });
                        }
                        if(node.boltSetData.washerID){
                          addLedgerPartToLedger({ id: node.boltSetData.washerID, quantity: 1 });
                        }
                      }
                    })
                  }
                  // if(region.boltCapData){
                  //   region.boltCapData.forEach(boltCap => {
                  //     if(boltCap.boltSetData.id){
                  //       addLedgerPartToLedger({ id: boltCap.boltSetData.id });
                  //     }
                  //   })
                  // }
                })
              }
            })
          }
        }
      }) 
    }
    return Object.keys(ledger).map(key => ledger[key]);
  },
  getLedgerPart: (state, getters) => (part) => {
    const ledgerPart = {
      id: part.id,
      quantity: 1,
      width: part.meshData.transform.scale?.[0], 
      height: part.meshData.transform.scale?.[1],
      length: part.meshData.transform.scale?.[2],
      customWidth: false,
      customHeight: false,
      customLength: false,
      requiredMachining: [],
    }

    if(part.extrudeObjectData.some(extrudeObjectData => extrudeObjectData.side == "F" || extrudeObjectData.side == "B")){
      ledgerPart.customLength = true
    }
    if(part.extrudeObjectData.some(extrudeObjectData => extrudeObjectData.side == "R" || extrudeObjectData.side == "L")){
      ledgerPart.customWidth = true;
    }
    if(part.extrudeObjectData.some(extrudeObjectData => extrudeObjectData.side == "U" || extrudeObjectData.side == "D")){
      ledgerPart.customHeight = true;
    }

    if(part.snapObjectData.snapAreaData){
      part.snapObjectData.snapAreaData.forEach(area => {
        if(area.snapRegionData){
          area.snapRegionData.forEach(region => {
            // console.log(region.instanceID);
            if(region.nodeData){
              region.nodeData.forEach(node => {
                if(node && node.requiredMachining && Array.isArray(node.requiredMachining)){
                  node.requiredMachining.forEach(requiredMachining => {
                    const keys = requiredMachining.split("_")
                    ledgerPart.requiredMachining.push({
                      key: keys.length > 2 ? keys[1]+"_"+keys[2] : null,
                      side: node.name.includes("F") ? "Front" : "Back",
                      location: keys.length > 1 && keys[1] == 'ENDTAP' ? null : (node.name + keys[0]).substring(1),
                      machining: keys.length > 1 ? keys[1] : null,
                      type: keys.length > 2 ? keys[2] : null,
                    })
                  })
                }
              })
            }
            if(region.lineData){
              region.lineData.forEach(line => {
                if(line && line.requiredMachining && Array.isArray(line.requiredMachining)){
                  line.requiredMachining.forEach(requiredMachining => {
                    const keys = requiredMachining.split("_")
                    ledgerPart.requiredMachining.push({
                      key: keys.length > 2 ? keys[1]+"_"+keys[2] : null,
                      slot: line.name,
                      machining: keys.length > 1 ? keys[1] : null,
                      type: keys.length > 2 ? keys[2] : null,
                      distance: keys.length > 3 ? keys[3] : null
                    })
                  })
                }
              })
            }
            if(region.panelData){
              region.panelData.forEach(panel => {
                if(panel && panel.requiredHoles){
                  panel.requiredHoles.forEach(requiredHole => {
                    ledgerPart.requiredMachining.push({
                      key: requiredHole.holeType.replace("_",""),
                      machining: "DRILLHOLE",
                      type: requiredHole.holeType.replace("_",""),
                      position: {
                        x: requiredHole.position[0] + part.meshData.transform.scale[0] / 2,
                        y: requiredHole.position[1] + part.meshData.transform.scale[1] / 2,
                        z: requiredHole.position[2] + part.meshData.transform.scale[2] / 2,
                      }
                    })
                  })
                }
              })
            }
          })
        }
      })
      ledgerPart.requiredMachining = ledgerPart.requiredMachining.sort((a,b) => {
        return a.type + a.side > b.type + b.side ? 1 : -1;
      });
    }
    return ledgerPart
  }
}

const mutations = {
  ...make.mutations([...Object.keys(state)]),
  savedFile: (state) => {
    state.saved = true;
    state.saving = false;
    setTimeout(() => {
      state.saved = false;
    }, 2000)
  },
  saveError: (state, value) => {
    state.saving_error = value;
    state.saving = false;
    setTimeout(() => {
      state.saving_error = null;
    }, 5000)
  },
  loaded: (state, id) => {
    state.loading = false;
    state.activeSaveFileId = id;
  },
  cancelLoading: (state) => {
    state.loading = false;
  },
}

const actions = {
  triggerSaveNewProject: ({commit}, {name}) => {
    dispatch('postMessage', "SaveNew:"+name).then(() => {
      commit("saving", true);
    }).catch(() => {

    });
  },
  triggerSaveExistingProject: ({commit}, {id, name}) => {
    commit('confirm', {
      title: "Overwrite project?",
      description: "You are about to overwrite an existing project.",
      start_label: "Save",
      stop_label: "Cancel",
      start: ()=>{
        dispatch('postMessage', "SaveExisting:"+id+"@"+name).then(() => {
          commit("saving", true);
        }).catch(() => {
          
        });
      },
      stop: ()=>{
      }
    }, {root: true});


    
  },
  saveFile: ({commit, state, rootState, rootGetters}, data) => {
    return new Promise((resolve, reject)=>{
      firebase.db.collection("user-data").doc(rootState.auth.user.uid).collection("save-files").doc(data.id).set(data).then(response => {
        console.log("Saved File! Lets Party!");
        commit("savedFile");
        commit("saveFiles", {...state.saveFiles, [data.id]: data});
        dispatch("postMessage", "SavedFile:"+data.id)
        commit("loaded", data.id);
        resolve();
      }).catch(error => {
        commit("saveError", error);
        dispatch("postMessage", "UnableToSaveFile:"+data.id)
        reject(error);
      })
    })
  },
  loadSaveFiles: ({commit, rootState}) => {
    if(rootState.auth.user && rootState.auth.user.uid){
      return new Promise((resolve, reject)=>{
        commit("saveFilesLoading", true);
        firebase.db.collection("user-data").doc(rootState.auth.user.uid).collection("save-files").get().then((querySnapshot) => {
          commit("saveFilesLoading", false);
          if(!querySnapshot.empty){
            const docs = {};
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              docs[data.id] = data;
            });
            commit("saveFiles", docs);
            resolve();
          }else{
            console.log("Empty Collection")
            commit("saveFiles", {});
            resolve();
          }
        }).catch(e => {
          commit("saveFilesLoading", false)
          console.log(e)
          reject(e)
        });
      })
    }
  },
  loadPublicSaveFiles: ({commit, rootState}) => {
    return new Promise((resolve, reject)=>{
      commit("publicSaveFilesLoading", true);
      firebase.db.collection("public-save-files").get().then((querySnapshot) => {
        commit("publicSaveFilesLoading", false);
        if(!querySnapshot.empty){
          const docs = {};
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            docs[data.id] = data;
          });
          // console.log(docs)
          commit("publicSaveFiles", docs);
          resolve();
        }else{
          console.log("Empty Collection")
          commit("publicSaveFiles", []);
          resolve();
        }
      }).catch(e => {
        commit("publicSaveFilesLoading", false)
        console.log(e)
        reject(e)
      });
    })
  },
  saveImage: ({commit, state, rootState}, {base64String, name}) => {
    return new Promise((resolve, reject)=>{
      fetch("data:image/png;base64,"+base64String).then((res) => {
        return res.arrayBuffer();
      }).then((buf)=>{
        const file = new File([buf], name+".png", {type:"image/png"});
        firebase.storage.ref(`${rootState.auth.user.uid}/${file.name}`).put(file, { customMetadata: {} }).then(response => {
          console.log("Image Uploaded!");
          dispatch("postMessage", "SavedImage:"+file.name);
          resolve(response);
        }).catch(err => {
          console.error(err);
          dispatch("postMessage", "UnableToSaveImage:"+file.name)
          this.$toast("Unable to save screenshot", {
            type: "error",
          });
          reject(err);
        })
      }).catch(error => {
        console.error(error);
        reject(error);
      })
    })
  },
  saveGlb: ({commit, state, rootState}, {base64String, name}) => {
    return new Promise((resolve, reject)=>{
      const bytes = window.atob(base64String);
      const blob = new Blob(bytes, {type: "model/gltf-binary"})
      const file = new File([blob], name+".glb");
      firebase.storage.ref(`${rootState.auth.user.uid}/${file.name}`).put(file, { customMetadata: {} }).then(response => {
        dispatch("postMessage", "SavedGlb:"+file.name);
        resolve(response);
      }).catch(err => {
        console.error(err);
        dispatch("postMessage", "UnableToSaveGlb:"+file.name)
        reject(err);
      })
    })
  },
  getImage: ({commit, state, rootState}, name) => {
    if(Object.keys(state.saveImages).includes(name)){
      return new Promise((resolve, reject)=>{
        resolve(state.saveImages[name])
      })
    }
    return new Promise((resolve, reject)=>{
      if(rootState.auth.user && rootState.auth.user.uid){
        firebase.storage.ref(`${rootState.auth.user.uid}/${name}.png`).getDownloadURL().then((url) => {
          commit("saveImages", {...state.saveImages, [name]: url})
          resolve(url);
        }).catch(function(error) {
          console.error(error);
          reject(error);   
        });
      }else{
        reject(error);
      }
    })
  },
  getGlb: ({commit, state, rootState}, name) => {
    return new Promise((resolve, reject)=>{
      if(rootState.auth.user && rootState.auth.user.uid){
        firebase.storage.ref(`${rootState.auth.user.uid}/${name}.glb`).getDownloadURL().then((url) => {
          resolve(url);
        }).catch(function(error) {
          console.error(error);
          reject(error);   
        });
      }else{
        reject(error);
      }
    })
  },
  getPublicGlb: ({commit, state, rootState}, {uid, name}) => {
    return new Promise((resolve, reject)=>{
      firebase.storage.ref(`${uid}/PUBLIC/${name}.glb`).getDownloadURL().then((url) => {
        resolve(url);
      }).catch(function(error) {
        console.error(error);
        reject(error);   
      });
    })
  },
  getFile: ({commit, state, rootState}, file) => {
    return new Promise((resolve, reject)=>{
      if(rootState.auth.user && rootState.auth.user.uid){
        firebase.storage.ref(`${rootState.auth.user.uid}/${file}`).getDownloadURL().then((url) => {
          resolve(url);
        }).catch(function(error) {
          console.error(error);
          reject(error);   
        });
      }else{
        reject(error);
      }
    })
  },
  getPublicImage: ({commit, state, rootState}, name) => {
    if(Object.keys(state.saveImages).includes(name)){
      return new Promise((resolve, reject)=>{
        resolve(state.saveImages[name])
      })
    }
    return new Promise((resolve, reject)=>{
      firebase.storage.ref(`PUBLIC/${name}.png`).getDownloadURL().then((url) => {
        commit("saveImages", {...state.saveImages, [name]: url})
        resolve(url);
      }).catch(function(error) {
        console.error(error);
        reject(error);
      });
    })
  },
  loadFromCloud: ({commit, state, rootState}, saveFile) =>{
    dispatch('postMessage', "Load:"+JSON.stringify(saveFile)).then(() => {
      commit("loading", true);
    })
    mixpanel.track('Load Project', {
      projectId: saveFile.id,
    });
  },
  confirmLoad: ({commit}, {id, name}) =>{
    commit('confirm', {
      title: "Discard Changes?",
      description: "You are about to discard unsaved changes.",
      start_label: "Discard",
      stop_label: "Cancel",
      start: ()=>{
        dispatch('postMessage', "LoadOverride:"+id);
      },
      stop: ()=>{
        commit("cancelLoading");
      }
    }, {root: true});
  },
  confirmNewProject: ({commit}) =>{
    commit('confirm', {
      title: "Discard Changes?",
      description: "You are about to discard unsaved changes.",
      start_label: "Discard",
      stop_label: "Cancel",
      start: ()=>{
        dispatch('postMessage', "NewProjectOverride:New");
        mixpanel.track('New Project');
      },
      stop: ()=>{
        commit("cancelLoading");
      }
    }, {root: true});
  },
  updateCustomField: ({commit, getters, rootState}, {saveFile, ledgerKey, field, value}) =>{
    return new Promise((resolve, reject)=>{
      const data = saveFile[field] ? saveFile[field] : {};

      data[ledgerKey] = value;

      // local save
      if(saveFile[field]){
        saveFile[field][ledgerKey] = value;
      }else{
        saveFile[field] = {
          [ledgerKey]: value
        }
      }
      commit("saveFiles", {...getters.saveFiles, [saveFile.id]: saveFile})

      firebase.db.collection("user-data").doc(rootState.auth.user.uid).collection("save-files").doc(saveFile.id).update({[field]: data}).then(() => {
        console.log('Updated!!!');
      }).catch(e => {
        console.log('Wooomp womp');
        console.log(e)
        reject(e)
      });
    })
  },
  publicizeFile: ({commit, getters, rootState}, {url, name}) => {
    return new Promise((resolve, reject)=>{
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = (event) => {
        const blob = xhr.response;
        var file = new File([blob], name);
        firebase.storage.ref(`${rootState.auth.user.uid}/PUBLIC/${name}.glb`).put(file, { customMetadata: {} }).then(response => {
          resolve(`${rootState.auth.user.uid}/${name}`);
        }).catch(error => {
          console.error(error)
          reject(error)
        })
      };
      xhr.open('GET', url);
      xhr.send();
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}